import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../utils/supabase'
import { setAuthTokenGetter, setCachedAuthToken, setPromoCode } from '../api/client'

interface AuthContextValue {
  session: Session | null
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  signInWithGoogle: () => Promise<void>
  signInWithFacebook: () => Promise<void>
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()
  const previousUserIdRef = useRef<string | null>(null)

  // Fetch the latest access token directly from Supabase's internal store,
  // forcing a refresh if the JWT has expired.  This avoids returning a stale
  // token from the React state closure (which can lag behind after tab
  // backgrounding or missed auto-refresh timers).
  const getAccessToken = useCallback(async () => {
    const { data: { session: current } } = await supabase.auth.getSession()
    if (!current) return null
    // If the access token has expired, force a refresh via the refresh_token
    if (current.expires_at && Date.now() / 1000 >= current.expires_at) {
      const { data: { session: refreshed } } = await supabase.auth.refreshSession()
      return refreshed?.access_token ?? null
    }
    return current.access_token
  }, [])

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setIsLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session)
        // Invalidate all queries on sign in/out to refetch with new auth state
        // This ensures debug-user cached data is replaced when a real user logs in
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          queryClient.invalidateQueries()
        }

        // Auto-apply VIP promo code for @meta.com users
        if (session?.user?.email?.endsWith('@meta.com')) {
          setPromoCode('DREAMWRIGHT_VIP')
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [queryClient])

  // Connect auth token getter to API client
  useEffect(() => {
    setAuthTokenGetter(getAccessToken)
  }, [getAccessToken])

  // Keep cached token in sync for asset URLs (used by <img> tags)
  useEffect(() => {
    setCachedAuthToken(session?.access_token ?? null)
  }, [session])

  // Invalidate queries when user changes (e.g., switching accounts)
  // This refetches data for the new user
  useEffect(() => {
    const currentUserId = session?.user?.id ?? null
    if (previousUserIdRef.current !== currentUserId && previousUserIdRef.current !== null) {
      // User changed - invalidate all queries to refetch
      queryClient.invalidateQueries()
    }
    previousUserIdRef.current = currentUserId
  }, [session?.user?.id, queryClient])

  const signInWithGoogle = async () => {
    const redirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      },
    })
    if (error) throw error
  }

  const signInWithFacebook = async () => {
    const redirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: redirectUrl,
      },
    })
    if (error) throw error
  }

  const signInWithMagicLink = async (email: string) => {
    const redirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    })
    return { error: error ? new Error(error.message) : null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    isLoading,
    isAuthenticated: !!session,
    signInWithGoogle,
    signInWithFacebook,
    signInWithMagicLink,
    signOut,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
